'use client';

import { cn } from '@/app/lib/utils';
import { FooterLogo, FooterColumns, FooterContact, FooterBottom } from './ui';
import { useFooter } from './lib/useFooter';
import { processFooterColumns, processContactInfo, processLegalInfo } from './lib/footer.utils';
import type { FooterData } from './interfaces';
import { theme } from '@workspace/ui/theme';

export interface FooterProps {
  className?: string;
  footerData?: FooterData;
}

export function Footer({ className = '', footerData: initialFooterData }: FooterProps) {
  const { footerData: fetchedFooterData } = useFooter();
  const typedFooter = (initialFooterData ?? fetchedFooterData) as FooterData | undefined;
  const footerBackgroundClass = theme === 'redRec' ? 'bg-primary' : 'bg-accent';

  const columns = processFooterColumns(typedFooter?.footerColums);
  const contactInfo = processContactInfo(typedFooter?.footerContact);
  const legalProps = processLegalInfo(typedFooter?.legalInfo);

  const hasContent = columns.length > 0 || (contactInfo?.contactItems?.length ?? 0) > 0;

  return (
    <footer className={cn(footerBackgroundClass, 'w-full relative', className)}>
      {hasContent && (
        <div className="porta-container px-md md:px-4xl md:pt-4xl md:pb-md-banner py-2xl">
          <div className="grid grid-cols-1 gap-y-3xl md:grid-cols-[auto_1fr_auto] md:gap-y-0">
            {/* Logo */}
            <div className="hidden md:block">
              <FooterLogo
                imageUrl={typedFooter?.footerLogo?.imageUrl}
                bynderLogo={typedFooter?.footerLogo?.bynderLogo}
                title={typedFooter?.footerLogo?.title}
                link={typedFooter?.footerLogo?.link}
              />
            </div>

            {/* Columns */}
            <div className="md:min-w-0 md:ml-gutter footer:ml-4xl">
              <FooterColumns columns={columns} />
            </div>

            {/* Contact */}
            <div className="md:justify-self-end md:ml-gutter footer:ml-3xl">
              <FooterContact contactInfo={contactInfo} socialLinks={[]} footerSocialLinks={typedFooter?.socialLinks} />
            </div>
          </div>
        </div>
      )}

      <FooterBottom
        copyrightText={legalProps.copyrightText}
        additionalText={legalProps.additionalText}
        legalLinks={legalProps.legalLinks}
      />
    </footer>
  );
}
